import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Check, Upload, Zap, RefreshCw, ArrowRight } from "lucide-react";
import { FeatureSlider } from "@/components/FeatureSlider";
import { SolutionsTicker } from "@/components/SolutionsTicker";
import owlIcon from "@/assets/owl-logo-thumbsup.png";

// Hero image loaded from public folder for LCP optimization (discoverable in initial HTML)
export default function Home() {
  return <div className="min-h-screen bg-background">
      <Navbar />
      
      <main>
      {/* Hero Section - Premium with background image */}
      <section className="relative pt-28 pb-16 sm:pt-32 sm:pb-20 md:pt-44 md:pb-36 overflow-hidden">
        {/* Background image - optimized for all devices */}
        <img 
          src="/hero-home.webp"
          alt="" 
          className="absolute inset-0 w-full h-full object-cover object-center"
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
        
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/90 to-background" />
        
        {/* Subtle animated glow accents */}
        <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-primary/10 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-glow-secondary/10 rounded-full blur-[100px] animate-pulse" style={{
        animationDelay: '1s'
      }} />
        
        <div className="container mx-auto px-5 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            {/* Headline - Optimized for mobile readability */}
            <h1 className="text-[1.75rem] leading-[1.2] sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-foreground mb-5 sm:mb-8 tracking-tight animate-fade-in">
              Financial Management for
              <br />
              <span className="text-primary">Hotels & Restaurants</span>
            </h1>
            
            {/* Subheadline - Better mobile text size */}
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-foreground/80 max-w-3xl mx-auto mb-8 sm:mb-12 leading-relaxed font-light px-2">
              AI-powered financial control for independent hotels, restaurants, and hospitality operators.
            </p>
            
            {/* CTAs - Refined sizing */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-8 sm:mb-12 px-4">
              <Button size="default" className="w-full sm:w-auto px-6 sm:px-8 h-11 sm:h-12 text-sm sm:text-base font-medium shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 active:scale-[0.98] hover:scale-[1.02] transition-all duration-300" asChild>
                <a href="/auth">
                  Get started
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </Button>
              <Button variant="outline" size="default" className="w-full sm:w-auto px-6 sm:px-8 h-11 sm:h-12 text-sm sm:text-base font-medium backdrop-blur-sm transition-all duration-300" asChild>
                <a href="https://calendly.com/centralflow/demo" target="_blank" rel="noopener noreferrer">
                  Book a demo
                </a>
              </Button>
            </div>
            
            {/* Trust Pills - Clean and minimal */}
            <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-center sm:gap-3 md:gap-4 px-4">
              <div className="inline-flex items-center px-4 py-2 sm:px-5 sm:py-2.5 rounded-full bg-foreground/5 backdrop-blur-sm border border-foreground/10 text-xs sm:text-sm text-foreground/85 transition-all duration-200 hover:bg-foreground/10 hover:border-foreground/20">
                <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-primary/20 flex items-center justify-center mr-2 sm:mr-2.5">
                  <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-primary" />
                </div>
                Built for Hospitality
              </div>
              <div className="inline-flex items-center px-4 py-2 sm:px-5 sm:py-2.5 rounded-full bg-foreground/5 backdrop-blur-sm border border-foreground/10 text-xs sm:text-sm text-foreground/85 transition-all duration-200 hover:bg-foreground/10 hover:border-foreground/20">
                <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-primary/20 flex items-center justify-center mr-2 sm:mr-2.5">
                  <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-primary" />
                </div>
                SMB Operator-friendly
              </div>
              <div className="inline-flex items-center px-4 py-2 sm:px-5 sm:py-2.5 rounded-full bg-foreground/5 backdrop-blur-sm border border-foreground/10 text-xs sm:text-sm text-foreground/85 transition-all duration-200 hover:bg-foreground/10 hover:border-foreground/20">
                <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-primary/20 flex items-center justify-center mr-2 sm:mr-2.5">
                  <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-primary" />
                </div>
                Works with your accountant
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom gradient fade */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent" />
      </section>

      {/* Solutions Ticker */}
      <SolutionsTicker />

      {/* Feature Slider Section */}
      <FeatureSlider />

      {/* How It Works Section */}
      <section id="how-it-works" className="py-16 sm:py-24 md:py-36 bg-background scroll-mt-20">
        <div className="container mx-auto px-5 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16 md:mb-20">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-foreground mb-4 sm:mb-6 tracking-tight px-2">
              Set up once. Stay in sync.
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto font-light px-4">
              Get your custom command center in 48 hours.
            </p>
          </div>

          {/* Steps - Equal height cards */}
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 md:gap-10 relative px-2 sm:px-0">
              
              {/* Step 1 */}
              <div className="relative group flex">
                <div className="bg-card border border-border rounded-2xl sm:rounded-3xl p-6 sm:p-8 text-center flex flex-col hover:border-primary/50 active:border-primary/60 hover:shadow-xl hover:shadow-primary/5 transition-all duration-500 hover:-translate-y-1 active:scale-[0.98] w-full">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-lg sm:text-xl font-bold mx-auto mb-4 sm:mb-6 relative z-10 group-hover:scale-110 transition-transform duration-300">
                    1
                  </div>
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-4 sm:mb-6 group-hover:bg-primary/30 transition-colors duration-300">
                    <Upload className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-foreground mb-2 sm:mb-3">
                    Import your book
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed flex-grow">
                    CSV/PDF exports from accounting, PMS, POS, bank—we handle it all.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="relative group flex">
                <div className="bg-card border border-border rounded-2xl sm:rounded-3xl p-6 sm:p-8 text-center flex flex-col hover:border-primary/50 active:border-primary/60 hover:shadow-xl hover:shadow-primary/5 transition-all duration-500 hover:-translate-y-1 active:scale-[0.98] w-full">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-lg sm:text-xl font-bold mx-auto mb-4 sm:mb-6 relative z-10 group-hover:scale-110 transition-transform duration-300">
                    2
                  </div>
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-card flex items-center justify-center mx-auto mb-4 sm:mb-6 group-hover:bg-card/80 transition-colors duration-300 border border-border">
                    <img src={owlIcon} alt="CentralFlow" className="w-10 h-10 sm:w-12 sm:h-12" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-foreground mb-2 sm:mb-3">
                    Get your Command Center
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed flex-grow">
                    Instant audit + command center built around your business.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="relative group flex">
                <div className="bg-card border border-border rounded-2xl sm:rounded-3xl p-6 sm:p-8 text-center flex flex-col hover:border-primary/50 active:border-primary/60 hover:shadow-xl hover:shadow-primary/5 transition-all duration-500 hover:-translate-y-1 active:scale-[0.98] w-full">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-lg sm:text-xl font-bold mx-auto mb-4 sm:mb-6 relative z-10 group-hover:scale-110 transition-transform duration-300">
                    3
                  </div>
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-4 sm:mb-6 group-hover:bg-primary/30 transition-colors duration-300">
                    <RefreshCw className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-foreground mb-2 sm:mb-3">
                    Run daily control
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed flex-grow">
                    Alerts, pacing, audits—no month-end surprises.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Text and Buttons */}
            <div className="mt-12 sm:mt-16 md:mt-20 text-center">
              <p className="text-lg sm:text-xl md:text-2xl font-medium text-foreground/90 leading-relaxed max-w-3xl mx-auto mb-8 sm:mb-10">
                Join hospitality operators who've replaced spreadsheet chaos with{' '}
                <span className="text-primary">real-time financial clarity.</span>
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                <Button size="default" className="w-full sm:w-auto px-6 sm:px-8 h-11 sm:h-12 text-sm sm:text-base font-medium shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 active:scale-[0.98] hover:scale-[1.02] transition-all duration-300" asChild>
                  <a href="/auth">
                    Get started
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </a>
                </Button>
                <Button variant="outline" size="default" className="w-full sm:w-auto px-6 sm:px-8 h-11 sm:h-12 text-sm sm:text-base font-medium backdrop-blur-sm transition-all duration-300" asChild>
                  <a href="https://calendly.com/centralflow/demo" target="_blank" rel="noopener noreferrer">
                    Book a demo
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section - Navy Dark Mode */}
      
      </main>

      <Footer />
    </div>;
}